// Shortens a long name in the MIDDLE, keeping its start and, where it fits,
// its end: "זה זה קבוצת קולינריה ואירוח בע״מ - מסעדת בואו" → "זה זה קבוצת…מסעדת בואו".
// Used by the navbar restaurant chip (owner, 2026-10-05).
//
// THE START ALWAYS WINS. In Hebrew the start of a name is its right-hand side
// and it is what people recognise ("פרייד צ׳יקן…"); a result that begins with
// "…" hides it, so that never happens. The start takes up to half the budget —
// or a second unit past that, unless it would push the last unit out
// ("שרים…טוסקה" beats "שרים בפתח…") — and the end fills what is left. When no
// end unit fits, the name is simply cut at the end ("פרייד צ׳יקן…").
//
// The name is cut between UNITS, not just words: a place ("רמת אביב", "פתח
// תקווה") or a branch ("סניף 2") is one unit, so two branches never collapse
// to the same "קפה…אביב". An end that is only a number ("…12") is never kept.
// A lone dash is never kept next to the "…". A first unit too long on its own
// is cut by characters.
//
// `max` counts the visible characters including the "…". The full name stays
// in the chip's tooltip, its accessible name and the picker list.
export const ELLIPSIS = '…';
const isDash = (w) => /^[-–—]+$/.test(w);
const isNumber = (u) => /^[\d\s#.]+$/.test(u);

// First words of multi-word Israeli place names: glued to the word after it.
const PLACE_PREFIX = new Set([
  'תל', 'רמת', 'ראשון', 'פתח', 'באר', 'כפר', 'הוד', 'קריית', 'קרית', 'בני', 'גבעת',
  'ראש', 'נס', 'מעלה', 'בית', 'נווה', 'גני', 'אור', 'זכרון', 'זיכרון', 'מגדל', 'עין',
  'אבן', 'נוף', 'טירת', 'מבשרת',
]);
// Branch markers: glued to a NUMBER after them ("סניף 2") — before a place
// they stay apart, so the place keeps its own unit ("סניף" "רמת אביב").
const BRANCH = new Set(['סניף', 'מס׳', "מס'", 'מספר', 'branch', 'no.']);

const toUnits = (s) => {
  const words = s.split(' ');
  const units = [];
  for (let i = 0; i < words.length; i += 1) {
    const w = words[i].toLowerCase();
    const next = words[i + 1];
    const joins = next !== undefined && !isDash(next)
      && (PLACE_PREFIX.has(w) || (BRANCH.has(w) && isNumber(next)));
    if (joins) { units.push(`${words[i]} ${next}`); i += 1; } else units.push(words[i]);
  }
  return units;
};

export function middleTruncate(text, max) {
  if (text == null) return text;
  const s = String(text).trim().replace(/\s+/g, ' ');
  if (!max || s.length <= max) return s;
  if (max < 2) return s.slice(0, max);
  const budget = max - 1;
  const units = toUnits(s);

  // Start: whole units from the front, up to half the budget (two units at
  // most past that). A unit that would push the last unit out is not taken
  // ("קפה…רמת אביב" beats "קפה קפה…") — unless that last unit is only a
  // number, which is not worth protecting.
  const headCap = Math.ceil(budget / 2);
  const last = units[units.length - 1];
  const protectLast = !isNumber(last);
  let head = '';
  let j = 0;
  for (; j < units.length; j += 1) {
    const cand = head ? `${head} ${units[j]}` : units[j];
    if (cand.length > budget) break;
    if (head) {
      if (cand.length > headCap && j >= 2) break;
      const lastFitsNow = head.length + last.length <= budget;
      const lastFitsAfter = j < units.length - 1 && cand.length + last.length <= budget;
      if (protectLast && lastFitsNow && !lastFitsAfter) break;
    }
    head = cand;
  }
  while (head && isDash(head.split(' ').pop())) head = head.split(' ').slice(0, -1).join(' ');
  if (!head) return `${s.slice(0, budget).trimEnd()}${ELLIPSIS}`;

  // End: whole units from the back, in what is left, never reaching the start.
  let tail = '';
  for (let i = units.length - 1; i >= j; i -= 1) {
    const cand = tail ? `${units[i]} ${tail}` : units[i];
    if (cand.length > budget - head.length) break;
    tail = cand;
  }
  while (tail && isDash(tail.split(' ')[0])) tail = tail.split(' ').slice(1).join(' ');
  if (isNumber(tail)) tail = '';

  return `${head}${ELLIPSIS}${tail}`;
}

// The company-form suffix ("בע״מ", "בע\"מ", "Ltd") says nothing about which
// restaurant this is, and as the last word it pushed the real name out of a
// short chip ("פרייד…בע״מ"). The chip drops it before shortening — also with
// curly quotes (as pasted from Word) and before a comma or full stop; a dash
// or comma it leaves dangling goes too. The full legal name stays in the
// tooltip, the accessible name and the picker list (owner, 2026-10-05).
const LEGAL_SUFFIX = /(^|\s)\(?(?:בע["״'׳”“’]{0,2}מ|ltd)\.?\)?(?=[\s,.)]|$)/giu;

export function withoutLegalSuffix(name) {
  if (!name) return name;
  const out = String(name)
    .replace(LEGAL_SUFFIX, '$1')
    .replace(/\s+([,.])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[-–—,]\s*|\s*[-–—,]$/g, '')
    .replace(/\s+[-–—]\s+(?=[-–—])/g, ' ')
    .trim();
  return out || String(name).trim();
}
